import * as React from "react";

import {
    Grid, GridColumn as Column,
    GridKeyDownEvent,
    GridItemChangeEvent,
    GridFilterChangeEvent,
    GridCustomCellProps
} from '@progress/kendo-react-grid';

import {
    SortDescriptor,
    filterBy,
    CompositeFilterDescriptor,
} from "@progress/kendo-data-query";

import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { Loader } from "@progress/kendo-react-indicators";

import { useGetProductsQuery } from '../../store/sunbright-api';
import { useEditProductMutation } from '../../store/sunbright-api';

export interface ProductsProps {
    category: number;
}
interface ProductRow {
    uid: number;
    category?: number;
    cdmvcode?: string;
    supplier?: string;
    description?: string;
    unitsperbox?: number;
    costperbox?: number;
    priceupdatedtime?: string;
    species?: string;
    type?: string;
}

const DATA_ITEM_KEY = 'uid';

const initialFilter: CompositeFilterDescriptor = {
    logic: "and",
    filters: []
};

const ProductsCategory = ({
    category,
}: ProductsProps) => {

    const { data: products, error: productsError, isLoading: productsLoading } = useGetProductsQuery(category + "");
    const [productsData, setProductsData] = React.useState<any>(null);

    const [editProduct, response] = useEditProductMutation();

    const [gridHeight, setGridHeight] = React.useState<string>("600px");

    const [dirtyRow, setDirtyRow] = React.useState<ProductRow>({ uid: 0 });
    const [addRow, setAddRow] = React.useState<ProductRow>({ uid: -1 });
    const [timerID, setTimerID] = React.useState<number>(-1);

    const [filterState, setFilterState] = React.useState(initialFilter);

    React.useEffect(() => {
        window.addEventListener("resize", handleResize);
        setGridHeight((window.innerHeight - 170) + "px");
    }, []);

    const handleResize = () => {
        setGridHeight((window.innerHeight - 170) + "px");
    };

    React.useEffect(() => {
        if (products) {
            setProductsData(products);
            setEdit(allInEdit(products));
       }
    }, [products]);


    const [edit, setEdit] = React.useState<EditDescriptor>({});

    interface EditSpec {
        [key: number]: string[];
    }

    const allInEdit = (data: any) => {
        let result:EditSpec = {};
        if (data && data.length > 0) {
            const editedColumns = Object.keys(data[0]); //the column names
            data.forEach((row:ProductRow) => {
                result[row.uid] = editedColumns;
            });
        }

        return (result as EditDescriptor);
    };

    const updateProduct = (row: any) => {
        editProduct(row)
            .unwrap()
            .then((payload) => { })
            .catch((error) => {
                console.log(error)
            });
    };

    const timedOut = (row:ProductRow) => {
        console.log("timeOut");
        if (row && (row.uid > 0)) {
            updateProduct(row);
        }
    };

    const handleItemChange = (event: GridItemChangeEvent) => {
        if (timerID >= 0) {
            window.clearTimeout(timerID);
        }

        const newData = productsData.map((row: ProductRow) => {
            if (row.uid === event.dataItem.uid) {
                const newRow: ProductRow = { ...row, [event.field!]: event.value };

                if (dirtyRow && (dirtyRow.uid > 0) && (dirtyRow.uid !== newRow.uid)) {
                    updateProduct(dirtyRow);
                    setDirtyRow({ uid: 0 });
                }
                if (newRow.uid > 0) {
                    setDirtyRow(newRow);
                    setTimerID(window.setTimeout(() => timedOut(newRow), 1500));
                } else {
                    setAddRow(newRow);
                }

                return newRow;
            }
            else {
                return row;
            }
        });

        setProductsData(newData);

    };

    const handleKeyDown = (event: GridKeyDownEvent) => {
        if ((event.nativeEvent.code === "Enter") && (addRow.uid === 0)) {
            updateProduct(addRow);
            setAddRow({ uid: -1 });
        }
    };

    const handleFilterChange = (event: GridFilterChangeEvent) => {
        setFilterState(event.filter);

        // Focus normally goes to first cell in grid after rendering filter results
        const element: HTMLInputElement = event?.nativeEvent?.srcElement;
        if (element) {
            window.setTimeout(() => element.focus(), 100);
        }
   }

    const SpeciesDropDownCell = (props: GridCustomCellProps) => {

        const handleChange = (e: DropDownListChangeEvent) => {
            if (props.onChange) {
                props.onChange({
                    dataIndex: 0,
                    dataItem: props.dataItem,
                    field: props.field,
                    syntheticEvent: e.syntheticEvent,
                    value: e.target.value
                });
            }
       };

        const { dataItem, isInEdit } = props;
        const field = props.field || '';
        const dataValue = dataItem[field];

        return (
            <td {...props.tdProps}>
                <DropDownList
                    style={{ width: "100%" }}
                   onChange={handleChange}
                    data={['Cat', 'Dog', 'Both']}
                    value={ dataValue }
                />
            </td>
        );
    };

    const TypeDropDownCell = (props: GridCustomCellProps) => {

        const handleChange = (e: DropDownListChangeEvent) => {
            if (props.onChange) {
                props.onChange({
                    dataIndex: 0,
                    dataItem: props.dataItem,
                    field: props.field,
                    syntheticEvent: e.syntheticEvent,
                    value: e.target.value
                });
            }
        };

        const { dataItem, isInEdit } = props;
        const field = props.field || '';
        const dataValue = dataItem[field];

        return (
            <td  {...props.tdProps}>
                <DropDownList
                    style={{width:"100%"}}
                    onChange={handleChange}
                    data={['Rabies', 'FVRCP', 'FeLV', 'FVRCP/Rabies', 'Da2PP', 'Parvovirus', 'Lyme', 'Lepto', 'Respiratory', 'DAPP/Lepto']}
                    value={dataValue}
                />
            </td>
        );
    };

   return (
        <React.Fragment>
            {!productsData ? (<Loader size="large" type="converging-spinner" />) : 
                (<div>
                    <Grid
                        style={{ height: gridHeight }}
                        className="productsGrid"
                        dataItemKey={DATA_ITEM_KEY}
                        pageable={false}
                        //data={productsData}
                        data={filterBy(productsData, filterState)}
                        //data={orderBy(filterBy(productsData, filterState), sortState)}
                        //autoProcessData={true}
                        //sortable={true}
                        //sort={sortState}
                        //onSortChange={(e: GridSortChangeEvent) => setSortState(e.sort)}
                        filterable={true}
                        filter={filterState}
                        onFilterChange={handleFilterChange}
                        resizable={true}
                        edit={edit}
                        editable={true}
                        onItemChange={handleItemChange}
                        onKeyDown={handleKeyDown}
                    //navigatable={{ mode: 'incell' as NavigatableMode }}
                >
                    <Column field="uid" filterable={false} title="Product ID" editable={false} width="86px" />
                    <Column field="cdmvcode" title="CDMV Code" width="156px" />
                    <Column field="supplier" title="Supplier" width="181px" />
                    <Column field="description" title="Description" />
                    {(category === 13) &&
                            <Column field="species" title="Species" filterable={false} width="86px"
                                cells={{ data: SpeciesDropDownCell }}
                            />}
                    {(category === 13) &&
                            <Column field="type" title="Type" filterable={false} width="144px"
                                cells={{ data: TypeDropDownCell }}
                            />}
                   <Column field="unitsperbox" title="Units per Box" editor="numeric" filterable={false} width="126px" />
                    <Column field="costperbox" title="Cost per box" editor="numeric" format="c2" filterable={false} width="119px" />
                    <Column field="priceupdatedtime" title="Price Updated" editor="date" format="{0:d}" filterable={false} width="136px" />
               </Grid>
                {(addRow.uid === 0) &&
                    <div style={{ color: "red", fontStyle: "italic" }}>
                        Press Enter when done entering row data.
                        </div>}
                </div>)
            }
    </React.Fragment>
    );
};
export default ProductsCategory;
